import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useToast } from '../components/ui/Toast';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.qrmenu.rw/api';

// Helper function to get proper image URL
const getImageUrl = (imageUrl: string | null): string | null => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
        return imageUrl;
    }
    // Uploads are served at domain root /uploads, not /api/uploads
    // Remove trailing /api from the API_URL
    const baseUrl = API_URL.replace(/\/api$/, '');
    return `${baseUrl}${imageUrl}`;
};

interface Establishment {
    id: number;
    name: string;
    logo_url: string | null;
    description: string | null;
    currency: string;
    is_menu_public: number | boolean;
    booking_enabled?: boolean;
}

interface BookingSettings {
    booking_enabled: boolean;
}

const EstablishmentsPage: React.FC = () => {
    const history = useHistory();
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [establishments, setEstablishments] = useState<Establishment[]>([]);
    const [loading, setLoading] = useState(true);
    const [bookingSettings, setBookingSettings] = useState<Record<number, BookingSettings>>({});

    useEffect(() => {
        document.title = 'Menu';
        fetchEstablishments();
    }, []);

    const fetchEstablishments = async () => {
        try {
            // Add timestamp to bust cache and prevent 304 responses
            const timestamp = new Date().getTime();
            const response = await axios.get(`${API_URL}/public/establishments?_t=${timestamp}`, {
                headers: {
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache'
                }
            });
            setEstablishments(response.data);

            // Fetch booking settings for each establishment
            response.data.forEach(async (est: Establishment) => {
                try {
                    const settingsRes = await axios.get(`${API_URL}/bookings/public/${est.id}/settings`);
                    setBookingSettings(prev => ({
                        ...prev,
                        [est.id]: settingsRes.data
                    }));
                } catch (e) {
                    // Booking not enabled for this establishment
                }
            });
        } catch (error) {
            console.error('Error fetching establishments:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleBookTable = (e: React.MouseEvent, establishment: Establishment) => {
        e.stopPropagation();
        history.push(`/book/${establishment.id}`);
    };

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #0B1120 0%, #060B18 100%)',
                color: 'white'
            }}>
                <div style={{
                    padding: '2rem',
                    borderRadius: '1rem',
                    background: 'rgba(85, 107, 47, 0.15)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(85, 107, 47, 0.3)'
                }}>
                    {t('common.loading')}
                </div>
            </div>
        );
    }

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #0B1120 0%, #060B18 50%, #0B1120 100%)',
            padding: '2rem 1rem'
        }}>
            <LanguageSwitcher />
            {/* Header */}
            <div style={{
                textAlign: 'center',
                marginBottom: '3rem',
                paddingTop: '2rem'
            }}>
                <h1 style={{
                    fontSize: '3rem',
                    fontWeight: 'bold',
                    background: 'linear-gradient(135deg, #556B2F 0%, #4B5320 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    marginBottom: '1rem'
                }}>
                    {t('establishments.title')}
                </h1>
                <p style={{
                    fontSize: '1.25rem',
                    color: 'rgba(255, 255, 255, 0.7)',
                    maxWidth: '600px',
                    margin: '0 auto'
                }}>
                    {t('establishments.subtitle')}
                </p>
            </div>

            {/* Establishments Grid */}
            <div style={{
                maxWidth: '1200px',
                margin: '0 auto',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: '2rem',
                padding: '0 1rem'
            }}>
                {establishments.map((establishment) => (
                    <div
                        key={establishment.id}
                        onClick={(e) => {
                            // Check if menu is publicly available (handle both boolean and integer from database)
                            const isPublic = establishment.is_menu_public === true || establishment.is_menu_public === 1;

                            if (!isPublic) {
                                showToast('Menu is not available for public viewing at this establishment.', 'warning');
                                return;
                            }

                            // Navigate to direct menu view without table requirement
                            const path = `/${establishment.id}/view`;
                            history.push(path);
                        }}
                        style={{
                            background: 'rgba(85, 107, 47, 0.1)',
                            backdropFilter: 'blur(10px)',
                            borderRadius: '1.5rem',
                            padding: '2rem',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            border: '1px solid rgba(85, 107, 47, 0.2)',
                            textAlign: 'center'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-8px)';
                            e.currentTarget.style.boxShadow = '0 20px 40px rgba(85, 107, 47, 0.25)';
                            e.currentTarget.style.borderColor = 'rgba(85, 107, 47, 0.5)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                            e.currentTarget.style.borderColor = 'rgba(85, 107, 47, 0.2)';
                        }}
                    >
                        {/* Logo */}
                        {establishment.logo_url ? (
                            <img
                                src={getImageUrl(establishment.logo_url) || ''}
                                alt={establishment.name}
                                style={{
                                    width: '120px',
                                    height: '120px',
                                    objectFit: 'contain',
                                    margin: '0 auto 1.5rem',
                                    borderRadius: '20px',
                                    background: 'rgba(255, 255, 255, 0.05)'
                                }}
                            />
                        ) : (
                            <div style={{
                                width: '120px',
                                height: '120px',
                                margin: '0 auto 1.5rem',
                                borderRadius: '20px',
                                background: 'linear-gradient(135deg, #556B2F 0%, #4B5320 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="white" style={{ width: '48px', height: '48px' }}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 .75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349M3.75 21V9.349m0 0a3.001 3.001 0 0 0 3.75-.615A2.993 2.993 0 0 0 9.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 0 0 2.25 1.016c.896 0 1.7-.393 2.25-1.015a3.001 3.001 0 0 0 3.75.614m-16.5 0a3.004 3.004 0 0 1-.621-4.72l1.189-1.19A1.5 1.5 0 0 1 5.378 3h13.243a1.5 1.5 0 0 1 1.06.44l1.19 1.189a3 3 0 0 1-.621 4.72M6.75 18h3.75a.75.75 0 0 0 .75-.75V13.5a.75.75 0 0 0-.75-.75H6.75a.75.75 0 0 0-.75.75v3.75c0 .414.336.75.75.75Z" />
                                </svg>
                            </div>
                        )}

                        {/* Name */}
                        <h2 style={{
                            fontSize: '1.5rem',
                            fontWeight: 'bold',
                            color: 'white',
                            marginBottom: '0.5rem'
                        }}>
                            {establishment.name}
                        </h2>

                        {/* Description */}
                        {establishment.description && (
                            <p style={{
                                fontSize: '1rem',
                                color: 'rgba(255, 255, 255, 0.6)',
                                marginBottom: '1rem',
                                lineHeight: '1.5'
                            }}>
                                {establishment.description}
                            </p>
                        )}

                        {/* Action Buttons */}
                        <div style={{
                            marginTop: '1.5rem',
                            display: 'flex',
                            gap: '0.75rem',
                            flexWrap: 'wrap'
                        }}>
                            {/* View Menu Button */}
                            <div style={{
                                padding: '0.75rem 1.5rem',
                                background: 'linear-gradient(135deg, #556B2F 0%, #4B5320 100%)',
                                color: 'white',
                                borderRadius: '0.5rem',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                flex: '1',
                                textAlign: 'center',
                                minWidth: '120px'
                            }}>
                                {t('establishments.viewMenu')}
                            </div>

                            {/* Book Table Button - Only show if booking is enabled */}
                            {bookingSettings[establishment.id]?.booking_enabled && (
                                <div
                                    onClick={(e) => handleBookTable(e, establishment)}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        background: 'linear-gradient(135deg, #556B2F 0%, #4B5320 100%)',
                                        color: 'white',
                                        borderRadius: '0.5rem',
                                        fontWeight: 'bold',
                                        cursor: 'pointer',
                                        flex: '1',
                                        textAlign: 'center',
                                        minWidth: '120px'
                                    }}
                                >
                                    {t('establishments.bookTable', 'Book Table')}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* No Establishments Message */}
            {establishments.length === 0 && !loading && (
                <div style={{
                    textAlign: 'center',
                    color: 'rgba(255, 255, 255, 0.5)',
                    padding: '4rem 2rem'
                }}>
                    <p style={{ fontSize: '1.25rem' }}>{t('establishments.noEstablishments')}</p>
                </div>
            )}

            {/* Footer */}
            <div style={{
                textAlign: 'center',
                marginTop: '4rem',
                padding: '2rem',
                color: 'rgba(255, 255, 255, 0.4)',
                fontSize: '0.875rem'
            }}>
                <p>Powered by QR Menu System</p>
                <p style={{ marginTop: '0.5rem' }}>
                    © {new Date().getFullYear()} All rights reserved
                </p>
            </div>
        </div>
    );
};

export default EstablishmentsPage;
